import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { TablePager, useClientPage } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const MINT = "#1FBF75";

const HEALTH = {
  capturing: { label: "Capturing", tone: "#137a47", bg: "#DFF7E7", dot: MINT,
    blurb: "A payment was just caught and uploaded — this device is working." },
  ready: { label: "Ready", tone: "#137a47", bg: "#E7F0FF", dot: "#2f6bd8",
    blurb: "Connected with notification access on — it'll catch payments as they arrive." },
  offline: { label: "Offline", tone: "#7a5400", bg: "#FFF3D6", dot: "#E6A700",
    blurb: "This phone hasn't checked in recently. Background capture may still run; open the app to reconnect." },
  needs_permission: { label: "Needs permission", tone: "#b3261e", bg: "#FBE9E7", dot: "#b3261e",
    blurb: "Notification access is OFF — grant it in the app (Settings → Notification access). Android revokes it after every app update." },
  backlog: { label: "Syncing backlog", tone: "#7a5400", bg: "#FFF3D6", dot: "#E6A700",
    blurb: "Catching payments — some are queued and uploading. This clears once the phone gets network." },
  // legacy aliases (backend no longer emits these)
  healthy: { label: "Ready", tone: "#137a47", bg: "#E7F0FF", dot: "#2f6bd8", blurb: "Connected — it'll catch payments as they arrive." },
  paused: { label: "Capture paused", tone: "#7a5400", bg: "#FFF3D6", dot: "#E6A700", blurb: "Turn on capture in the app." },
};

function timeAgo(iso) {
  if (!iso) return "never";
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function Pill({ ok, on, off }) {
  const good = ok === true;
  const bad = ok === false;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "JetBrains Mono, monospace", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100, border: `2px solid ${INK}`, background: good ? "#DFF7E7" : bad ? "#FBE9E7" : "#F1ECE0", color: good ? "#137a47" : bad ? "#b3261e" : "#6a6478" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: good ? MINT : bad ? "#b3261e" : "#9a93a8" }} />
      {ok == null ? "—" : good ? on : off}
    </span>
  );
}

export default function Devices() {
  const [devices, setDevices] = useState(null);
  // A merchant has a handful of phones and the summary above needs all of them,
  // so this list is paged in the browser (10 per page).
  const { slice: pageDevices, pager } = useClientPage(devices, 10);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/device/list");
      setDevices(data);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // live-ish refresh
    return () => clearInterval(t);
  }, [load]);

  const online = (devices || []).filter((d) => d.online).length;
  // "Ready" (online + access on) OR "capturing" both mean the phone will catch
  // payments — that's what the merchant cares about. Reserve the live count for
  // devices that are actually in a working state (not offline/needs-permission).
  const working = (devices || []).filter((d) => d.health === "capturing" || d.health === "ready" || d.health === "healthy" || d.health === "backlog").length;
  const totalCaught = (devices || []).reduce((s, d) => s + (d.credits_settled || 0), 0);

  return (
    <div className="ui-page" data-testid="devices-page">
      {/* summary */}
      <div className="ui-grid ui-grid-3 dv-stats">
        {[
          { l: "Devices online", v: `${online}/${(devices || []).length}`, id: "dev-online" },
          { l: "Working now", v: String(working), id: "dev-capturing" },
          { l: "Payments auto-caught · all time", v: String(totalCaught), id: "dev-caught" },
        ].map((s) => (
          <div key={s.l} className="ui-card ui-stat" data-testid={s.id}>
            <span className="ui-eyebrow">{s.l}</span>
            <span className="ui-stat-value" style={{ fontSize: 28 }}>{s.v}</span>
          </div>
        ))}
      </div>

      <div className="ui-banner is-info" data-testid="devices-explainer" style={{ flexDirection: "column", gap: 4 }}>
        <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 15 }}>How auto-settlement works</div>
        <p style={{ fontSize: 13, color: "#51495f", margin: 0, lineHeight: 1.55 }}>
          The SuperCash app on your phone reads your bank/UPI payment notifications and matches each one to a payment link — settling it in seconds. Keep at least one device <strong>Capturing</strong>. After every app update, Android turns notification access off, so you must re-grant it in the app.
        </p>
      </div>

      {devices == null ? (
        <div className="ui-card ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <span className="ui-mono ui-faint" style={{ fontSize: 12 }}>Loading devices…</span>
          {[0, 1, 2].map((i) => <div key={i} className="ui-skel" style={{ height: 16, width: `${85 - i * 15}%` }} />)}
        </div>
      ) : devices.length === 0 ? (
        <div className="ui-card" data-testid="devices-empty">
          <div className="ui-empty" style={{ padding: "44px 20px" }}>
            <span style={{ width: 60, height: 60, borderRadius: 16, background: "#ECE8FB", border: `2.5px solid ${INK}`, display: "inline-grid", placeItems: "center", marginBottom: 8, fontSize: 26 }}>📱</span>
            <b style={{ fontSize: 18 }}>No devices connected</b>
            <span style={{ fontSize: 13.5, lineHeight: 1.5, maxWidth: 380 }}>
              Install the SuperCash Android app, log in with this account, and grant notification access. Your phone will appear here and start auto-catching payments.
            </span>
          </div>
        </div>
      ) : (
        <div className="dv-list">
          {pageDevices.map((d) => {
            const h = HEALTH[d.health] || HEALTH.offline;
            return (
              <section key={d.id} className="ui-card" data-testid={`device-${d.id}`}>
                <header className="ui-card-head" style={{ alignItems: "flex-start" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 200px" }}>
                    <span style={{ width: 44, height: 44, flex: "none", borderRadius: 12, background: d.online ? "#DFF7E7" : "#F1ECE0", border: `2px solid ${INK}`, display: "grid", placeItems: "center", fontSize: 20 }}>📱</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="ui-card-title ui-trunc">{d.device_name}</div>
                      <div className="ui-mono ui-muted" style={{ fontSize: 11, marginTop: 2, overflowWrap: "anywhere" }}>
                        {d.platform} · v{d.app_version || "?"} · seen {timeAgo(d.last_seen_at)}
                      </div>
                    </div>
                  </div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 100, border: `2px solid ${INK}`, background: h.bg, color: h.tone, fontFamily: "JetBrains Mono, monospace", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }} data-testid={`device-health-${d.id}`}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: h.dot }} /> {h.label}
                  </span>
                </header>

                <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ fontSize: 13, color: "#51495f", lineHeight: 1.55 }}>{h.blurb}</div>

                  <div className="ui-row" style={{ gap: 8 }}>
                    <Pill ok={d.online} on="Connected" off="Offline" />
                    <Pill ok={d.notif_access_granted} on="Notif access ✓" off="Notif access ✗" />
                    <Pill ok={d.capture_enabled} on="Capture on" off="Capture off" />
                  </div>

                  <div className="dv-metrics">
                    <Metric label="Credits seen · total" value={d.credits_caught} />
                    <Metric label="Payments matched · total" value={d.credits_settled} accent />
                    <Metric label="Queued to send" value={d.queued_count ?? "—"} />
                    <Metric label="Last catch" value={timeAgo(d.last_capture_at)} small />
                  </div>
                </div>
              </section>
            );
          })}
          <TablePager testId="devices-pager" boxed {...pager} />
        </div>
      )}

      <style>{`
        .dv-list { display: flex; flex-direction: column; gap: 14px; }
        .dv-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
        .dv-metric { background: var(--ui-surface-2); border: 2px solid var(--ui-line); border-radius: 12px; padding: 10px 12px; min-width: 0; }
        @media (max-width: 900px) { .dv-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 900px) and (min-width: 481px) { [data-testid="devices-page"] .dv-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 480px) { .dv-metrics { gap: 8px; } }
      `}</style>
    </div>
  );
}

function Metric({ label, value, accent, small }) {
  return (
    <div className="dv-metric">
      <div className="ui-eyebrow" style={{ fontSize: 10, color: "#9a93a8" }}>{label}</div>
      <div style={{ fontFamily: small ? "JetBrains Mono, monospace" : "Inter, sans-serif", fontWeight: small ? 700 : 800, fontSize: small ? 13 : 20, marginTop: 4, color: accent ? VIOLET : INK, overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );
}
